/**
 * Persistence for the session grant. This is a CACHE, not a source of truth — per the frontend
 * brief, anything here must be re-derivable from the venue reference (URL, or a re-run of Find
 * Me). Never make a client depend on this surviving.
 */

export interface SessionStorage {
  read(): PersistedSession | null;
  write(session: PersistedSession): void;
  clear(): void;
}

export interface PersistedSession {
  sessionToken: string;
  requesterId: string;
  venueRef: string;
  surface: string;
  /** ISO 8601, mirrors `expires_at` from the grant. */
  expiresAt: string;
}

const STORAGE_KEY = "touchscreen:session";

/** Guards against non-browser environments (SSR, tests) and a full/blocked storage quota. */
export function createLocalStorageSessionStorage(storage: Storage = safeLocalStorage()): SessionStorage {
  return {
    read() {
      try {
        const raw = storage.getItem(STORAGE_KEY);
        if (!raw) return null;
        return JSON.parse(raw) as PersistedSession;
      } catch {
        return null;
      }
    },
    write(session) {
      try {
        storage.setItem(STORAGE_KEY, JSON.stringify(session));
      } catch {
        // Storage full or blocked (private mode). The grant still works in-memory for this tab.
      }
    },
    clear() {
      try {
        storage.removeItem(STORAGE_KEY);
      } catch {
        // Nothing to do — best-effort clear.
      }
    },
  };
}

/** An in-memory storage for tests, or environments with no `localStorage`. */
export function createMemorySessionStorage(): SessionStorage {
  let current: PersistedSession | null = null;
  return {
    read: () => current,
    write: (session) => {
      current = session;
    },
    clear: () => {
      current = null;
    },
  };
}

function safeLocalStorage(): Storage {
  if (typeof window !== "undefined" && window.localStorage) {
    return window.localStorage;
  }
  // Fall back to a no-op-ish shim; createLocalStorageSessionStorage's try/catch absorbs failures.
  return {
    getItem: () => null,
    setItem: () => undefined,
    removeItem: () => undefined,
    clear: () => undefined,
    key: () => null,
    length: 0,
  } as Storage;
}
